import { EMAIL_PATTERN } from '@/lib/email';
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { Check, Fingerprint, Lock, Mail, ShieldCheck } from 'lucide-react';
import { adminAuthController, adminPasskeyController } from '@/api/generated/endpoints';
import { errorMessage } from '@/api/errors';
import { unwrap } from '@/api/http';
import { useAuth } from '@/auth/AuthProvider';
import { VeoMark } from '@/components/VeoMark';
import { Alert, Button, Field, Input, Starfield, toast } from '@/ui';
import { addPasskey, isPasskeyCancel, passkeysSupported, signInWithPasskey } from './passkey';
import { PasswordInput } from './PasswordInput';

type Step = 'credentials' | 'code' | 'offer';

function CredentialsForm({ pending, onSubmit }: { pending: boolean; onSubmit(v: { email: string; password: string }): void }) {
  const { t } = useTranslation('auth');
  const { register, handleSubmit, formState } = useForm<{ email: string; password: string }>();
  const { errors } = formState;
  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      <Field label={t('email')} error={errors.email?.message}>
        <Input
          prefix={<Mail />}
          type="email"
          autoComplete="username"
          autoFocus
          {...register('email', {
            required: t('emailRequired'),
            pattern: { value: EMAIL_PATTERN, message: t('emailInvalid') },
            validate: (v) => VEO_EMAIL.test(v.trim()) || t('emailDomain'),
          })}
        />
      </Field>
      <Field label={t('password')} error={errors.password?.message}>
        <PasswordInput prefix={<Lock />} autoComplete="current-password" {...register('password', { required: t('passwordRequired') })} />
      </Field>
      <Button type="submit" variant="primary" size="lg" loading={pending} className="mt-1 w-full">
        {t('continue')}
      </Button>
    </form>
  );
}

/** Only company accounts sign in (the gateway enforces it too). */
const VEO_EMAIL = /@veoride\.com$/i;

/** A complete sign-in code: six digits (spaces around, from a paste, are fine). */
const CODE = /^\s*\d{6}\s*$/;

function CodeForm({ email, pending, onSubmit, onBack }: { email: string; pending: boolean; onSubmit(v: { code: string }): void; onBack(): void }) {
  const { t } = useTranslation('auth');
  const { register, handleSubmit, formState, control } = useForm<{ code: string }>();
  // Signs in as soon as the sixth digit is typed (or the emailed code is pasted); a code that
  // failed is not resent until it changes.
  const code = useWatch({ control, name: 'code' }) ?? '';
  const tried = useRef<string | null>(null);
  useEffect(() => {
    const value = code.trim();
    if (pending || !CODE.test(code) || tried.current === value) return;
    tried.current = value;
    onSubmit({ code: value });
  }, [code, pending, onSubmit]);
  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      <p className="m-0 text-base text-ink">
        <Trans t={t} i18nKey="codeSent" values={{ email }} components={{ b: <b /> }} />
      </p>
      <Field label={t('code')} error={formState.errors.code?.message}>
        <Input
          prefix={<ShieldCheck />}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={8}
          autoFocus
          boxClassName="pointer-coarse:h-12"
          className="tracking-[0.3em]"
          {...register('code', { required: t('codeRequired'), pattern: { value: CODE, message: t('codeRequired') } })}
        />
      </Field>
      <Button type="submit" variant="primary" size="lg" loading={pending} className="mt-1 w-full">
        {t('verify')}
      </Button>
      <Button variant="link" onClick={onBack} className="self-center">
        {t('differentAccount')}
      </Button>
    </form>
  );
}

/** The passkey way in, above the password form: one tap, no password and no code. */
function PasskeySignIn({ pending, onClick }: { pending: boolean; onClick(): void }) {
  const { t } = useTranslation('auth');
  return (
    <div className="mb-5 flex flex-col gap-2">
      <Button variant="secondary" size="lg" icon={<Fingerprint />} loading={pending} onClick={onClick} className="w-full">
        {t('passkey.signIn')}
      </Button>
      <p className="m-0 text-center text-sm text-ink-muted">{t('passkey.signInHint')}</p>
      <div className="mt-3 flex items-center gap-3 text-sm text-ink-muted" aria-hidden>
        <span className="h-px flex-1 bg-line" />
        {t('passkey.or')}
        <span className="h-px flex-1 bg-line" />
      </div>
    </div>
  );
}

/**
 * After every password + code sign-in of an admin without a passkey, an invitation to add one (the
 * session is fresh, which the gateway requires). Optional: "Not now" goes straight in.
 */
function PasskeyOffer({ onDone }: { onDone(): void }) {
  const { t } = useTranslation('auth');
  const [pending, setPending] = useState(false);
  const setUp = async () => {
    setPending(true);
    try {
      await addPasskey();
      toast.success(t('passkey.added'));
      onDone();
    } catch (err) {
      if (!isPasskeyCancel(err)) toast.error(t('passkey.addFailed'));
    } finally {
      setPending(false);
    }
  };
  return (
    <div className="flex flex-col gap-4">
      <p className="m-0 text-base text-ink">{t('passkey.offerBody')}</p>
      <ul className="m-0 flex list-none flex-col gap-2 p-0 text-sm text-ink-muted">
        {(['offerHow', 'offerSafe', 'offerOptional', 'offerWhere'] as const).map((k) => (
          <li key={k} className="flex gap-2">
            <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden />
            {t(`passkey.${k}`)}
          </li>
        ))}
      </ul>
      <Button variant="primary" size="lg" icon={<Fingerprint />} loading={pending} onClick={setUp} className="w-full">
        {t('passkey.setUp')}
      </Button>
      <Button variant="link" className="self-center" onClick={onDone}>
        {t('passkey.notNow')}
      </Button>
    </div>
  );
}

/**
 * Sign-in with a passkey, or in two steps: company email and password, then the code emailed there. Like the legacy admin console, it does
 * not act on requirePasswordUpdate (only the technician app forces a change); admins change
 * their password from the profile page.
 */
export function LoginPage() {
  const { t } = useTranslation('auth');
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');

  const requestCode = useMutation({
    mutationFn: async (v: { email: string; password: string }) => {
      unwrap(await adminAuthController.getAuthCode({ authDTO: { ...v, email: v.email.trim() } }));
      return v.email.trim();
    },
    onSuccess: (e) => {
      setEmail(e);
      setStep('code');
    },
  });

  const enter = () => navigate('/', { replace: true });

  const verify = useMutation({
    mutationFn: async (v: { code: string }) => unwrap(await adminAuthController.verifyAuthCode({ authDTO: { email, code: v.code.trim() } })),
    onSuccess: async (t) => {
      if (!t?.token) return;
      signIn(t.token);
      // recommend a passkey to those without one (never required)
      if (passkeysSupported()) {
        const mine = await adminPasskeyController.list().catch(() => null);
        if (mine?.code === 0 && (mine.data ?? []).length === 0) {
          setStep('offer');
          return;
        }
      }
      enter();
    },
  });

  const passkey = useMutation({
    mutationFn: signInWithPasskey,
    onSuccess: (t) => {
      if (!t?.token) return;
      signIn(t.token);
      enter();
    },
  });

  const passkeyError = passkey.error && !isPasskeyCancel(passkey.error) ? passkey.error : null;
  const error = requestCode.error ?? verify.error ?? passkeyError;
  const subtitle = step === 'credentials' ? t('subtitle') : step === 'code' ? t('verifyTitle') : t('passkey.offerTitle');

  return (
    <div className="relative isolate grid min-h-screen place-items-center bg-canvas px-4 py-6 font-sans text-ink">
      <Starfield className="-z-10" opacity={0.35} density={0.00008} />
      <main className="w-full max-w-[400px] rounded-xl border border-line bg-surface p-8 shadow-sm">
        <VeoMark size={40} />
        <h1 className="m-0 mt-3 font-display text-2xl font-semibold text-heading">Manhattan</h1>
        <p className="m-0 mb-6 mt-0.5 text-base text-ink-muted">{subtitle}</p>
        {error && <Alert tone="danger" title={errorMessage(error)} className="mb-4" />}
        {step === 'credentials' && (
          <>
            {passkeysSupported() && <PasskeySignIn pending={passkey.isPending} onClick={() => passkey.mutate()} />}
            <CredentialsForm pending={requestCode.isPending} onSubmit={(v) => requestCode.mutate(v)} />
          </>
        )}
        {step === 'code' && (
          <CodeForm email={email} pending={verify.isPending} onSubmit={(v) => verify.mutate(v)} onBack={() => setStep('credentials')} />
        )}
        {step === 'offer' && <PasskeyOffer onDone={enter} />}
      </main>
    </div>
  );
}
